import type { MbcBrandContext } from "@nlc/shared-types";

import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { useGetBrandById } from "@/features/brands/shared/hooks/useGetBrandById";
import { NOT_STARTED_QUALITY } from "@/features/request/mbc-review/lib/brief-score-summary";

import type { MbcRequestType } from "./lib/request-draft-api";

import { BrandGate } from "./components/BrandGate";
import { BriefSidebar } from "./components/BriefSidebar";
import { MBCChatHeader } from "./components/MBCChatHeader";
import { MBCConversation } from "./components/MBCConversation";
import { useLeaveMbcFlow } from "./hooks/useLeaveMbcFlow";
import { mapBrandContext } from "./lib/map-brand-context";

type MBCChatPageProps = {
  /** Numeric design-type id coming from the request-create picker. */
  designType?: number;
  /** Project the request is being created for; scopes the brand list/drafts. */
  projectId?: number;
  /** Request type of the design type picked; the draft is created with it. */
  requestType?: MbcRequestType;
  /** Resume: brand of the draft being reopened (re-grounds the agent). */
  resumeBrandId?: number;
  /** Resume: the draft's resource id. */
  resumeResourceId?: string;
  /** Resume: the draft's thread id. Presence = resume mode. */
  resumeThreadId?: string;
};

export function MBCChatPage({
  designType,
  projectId,
  requestType,
  resumeBrandId,
  resumeResourceId,
  resumeThreadId,
}: MBCChatPageProps) {
  const navigate = useNavigate();
  const handleBack = useLeaveMbcFlow();

  const [brand, setBrand] = useState<{
    context: MbcBrandContext | null;
    resolved: boolean;
  }>({ context: null, resolved: false });

  // Captured on mount: the chat writes its own threadId into the URL (so a
  // refresh can recover the session), and that must not flip a live fresh
  // conversation into resume mode mid-flight.
  const [isResume] = useState(() =>
    Boolean(resumeThreadId && resumeResourceId),
  );
  const { data: resumeBrand, isFetching: isFetchingResumeBrand } =
    useGetBrandById(resumeBrandId ?? 0);

  const handleCreateNewBrand = useCallback(() => {
    void navigate({ to: "/brands/create" });
  }, [navigate]);

  const shell = (children: React.ReactNode) => (
    <div className="mx-auto flex h-full min-h-0 max-w-[1600px] flex-col overflow-hidden bg-bg-tertiary">
      {children}
    </div>
  );

  if (isResume) {
    if (resumeBrandId && isFetchingResumeBrand) {
      return shell(
        <>
          <MBCChatHeader
            brandId={resumeBrandId}
            designType={designType}
            onBack={handleBack}
          />
          <p className="p-3xl text-sm text-text-tertiary">Loading draft…</p>
        </>,
      );
    }

    return shell(
      <MBCConversation
        brand={resumeBrand ? mapBrandContext(resumeBrand) : null}
        designType={designType}
        projectId={projectId}
        requestType={requestType}
        resume={{ resourceId: resumeResourceId!, threadId: resumeThreadId! }}
      />,
    );
  }

  if (brand.resolved) {
    return shell(
      <MBCConversation
        brand={brand.context}
        designType={designType}
        projectId={projectId}
        requestType={requestType}
      />,
    );
  }

  return shell(
    <>
      <MBCChatHeader designType={designType} onBack={handleBack} />

      <div className="flex min-h-0 flex-1 gap-3xl p-3xl">
        <BrandGate
          onCreateNewBrand={handleCreateNewBrand}
          onResolved={(context) => setBrand({ context, resolved: true })}
          projectId={projectId}
        />
        <BriefSidebar
          canAddFiles={false}
          isCalculating={false}
          memory={null}
          onAddAssets={() => {}}
          onFilesChanged={() => {}}
          quality={NOT_STARTED_QUALITY}
        />
      </div>
    </>,
  );
}
